import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import { useAuth } from '../../hooks/useAuth';
import { CopmanyUser } from '../../types/api';
import companyService from '../../services/company.service';
import jobService from '../../services/job.service';
import { useCompany } from '../../context/CompanyContext';

export default function CompanyDashboard() {
  const { user } = useAuth();
  const {companyDetails, jobPostCount} = useCompany();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [jobs, setJobs] = useState<Array<{ id: number; title: string; applicants: number; status: string }>>([]);
  const {jobPosts} = useCompany();
  const [pipeline, setPipeline] = useState<Array<{ stage: string; count: number }>>([]);

  // Check if registration is pending
  const isPendingApproval = useMemo(()=> {
    return companyDetails?.verificationStatus == 'Pending'
  },[ companyDetails?.verificationStatus]);

  const stats = useMemo(() => {
    const openJobs = jobs.filter(j => j.status === 'Open').length;
    const totalApplicants = jobs.reduce((sum, j) => sum + (Number(j.applicants) || 0), 0);
    const interviews = pipeline.find(p => p.stage.toLowerCase() === 'interview')?.count || 0;
    return [
      { label: 'Open jobs', value: jobPostCount || '-' },
      { label: 'Active candidates', value: totalApplicants },
      { label: 'Interviews this week', value: interviews },
      { label: 'Time‑to‑hire (median)', value: '—' },
    ];
  }, [jobs, pipeline]);

  useEffect(() => {
    const fetchData = async () => {
      if (!(user && user.role === 'Company' && (user as CopmanyUser).companyId)) {
        setLoading(false);
        return;
      }
      setError(null);
      try {
        // Jobs
        const jobsRes = jobPosts;
        const apiJobs = (jobsRes || []).map((p) => ({
          id: p.postId,
          title: p.jobTitle || 'Untitled role',
          applicants: 0,
          status: p.isDraft ? 'Draft' : (p.isActive ? 'Open' : 'Paused')
        }));
        setJobs(apiJobs);

        // Dashboard/pipeline (if available)
        try {
          // const dashboardRes = await companyService.getDashboardData();
          // const stages = dashboardRes.data?.pipeline || [];
          // setPipeline(stages);
        } catch (e) {
          // Fallback: derive a minimal pipeline approximation if API not available
          const applied = apiJobs.reduce((sum, j) => sum + (Number(j.applicants) || 0), 0);
          setPipeline([
            { stage: 'Applied', count: applied },
          ]);
        }
      } catch (err: any) {
        setError(err?.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, jobPosts]);

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="space-y-8">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl">{error}</div>
          )}
          {/* Welcome Banner (single visible header) */}
          <div className="rounded-2xl p-5 sm:p-8 bg-[#87DE88]">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
              <div>
                <div className="mb-2">
                  <h1 className="text-xl sm:text-3xl font-extrabold text-primary-black">Company Dashboard</h1>
                </div>
                <p className="text-primary-black/90 text-base sm:text-lg">Welcome back, {user?.firstName || 'Company'}!</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                <Link to="/company/profile" className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto !bg-black hover:!bg-gray-800 !text-white !border-black hover:!border-gray-800">Company Profile</Button>
                </Link>
                <Link to="/company/jobs/create" className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto !bg-black hover:!bg-gray-800 !text-white !border-black hover:!border-gray-800">Post New Job</Button>
                </Link>
              </div>
            </div>
            {isPendingApproval && (
              <div className="sm:hidden mt-4 flex items-center space-x-3 px-4 py-3 bg-gradient-to-r from-yellow-100 to-amber-100 border-2 border-yellow-400 rounded-xl shadow-lg">
                <svg className="h-5 w-5 text-yellow-600 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="flex flex-col">
                  <span className="text-base font-bold text-yellow-800">Agents Are Reviewing</span>
                  <span className="text-xs text-yellow-700">Documents under verification</span>
                </div>
              </div>
            )}
          </div>


          {/* Stats Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {(loading ? [
              { label: 'Open jobs', value: '—' },
              { label: 'Active candidates', value: '—' },
              { label: 'Interviews this week', value: '—' },
              { label: 'Time‑to‑hire (median)', value: '—' },
            ] : stats).map((s, index) => (
              <div key={s.label} className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs sm:text-sm font-medium text-gray-600 mb-1">{s.label}</div>
                    <div className="text-xl sm:text-3xl font-bold text-primary-black">{s.value}</div>
                  </div>
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center ${
                    index === 0 ? 'bg-primary-lightGreen/20' :
                    index === 1 ? 'bg-green-100' :
                    index === 2 ? 'bg-purple-100' :
                    'bg-orange-100'
                  }`}>
                    {index === 0 && (
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-primary-lightGreen" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                      </svg>
                    )}
                    {index === 1 && (
                        <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
                      </svg>
                    )}
                    {index === 2 && (
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    )}
                    {index === 3 && (
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-2xl p-5 sm:p-8 shadow-lg border border-gray-100">
            <h2 className="text-xl font-semibold text-primary-black mb-6">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              <Link to="/company/jobs/create" className="group">
                <div className="p-6 border-2 border-dashed border-gray-300 rounded-2xl text-center hover:border-primary-lightGreen hover:bg-primary-lightGreen/5 transition-all duration-200 hover:shadow-lg">
                  <div className="w-12 h-12 mx-auto mb-4 bg-primary-lightGreen/10 rounded-xl flex items-center justify-center group-hover:bg-primary-lightGreen/20 transition-colors">
                    <svg className="w-6 h-6 text-primary-lightGreen" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-primary-black mb-1">Post New Job</p>
                  <p className="text-xs text-gray-500">Create a job posting</p>
                </div>
              </Link>
              <Link to="/company/applications" className="group">
                <div className="p-6 border-2 border-dashed border-gray-300 rounded-2xl text-center hover:border-primary-lightGreen hover:bg-primary-lightGreen/10 transition-all duration-200 hover:shadow-lg">
                  <div className="w-12 h-12 mx-auto mb-4 bg-primary-lightGreen/20 rounded-xl flex items-center justify-center group-hover:bg-primary-lightGreen/30 transition-colors">
                    <svg className="w-6 h-6 text-primary-lightGreen" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-primary-lightGreen mb-1">View Applications</p>
                  <p className="text-xs text-gray-500">Manage candidates</p>
                </div>
              </Link>
              <Link to="/company/profile" className="group">
                <div className="p-6 border-2 border-dashed border-green-300 rounded-2xl text-center hover:border-green-500 hover:bg-green-50 transition-all duration-200 hover:shadow-lg">
                  <div className="w-12 h-12 mx-auto mb-4 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-200 transition-colors">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-green-600 mb-1">Company Profile</p>
                  <p className="text-xs text-gray-500">Update information</p>
                </div>
              </Link>
              <Link to="/company/jobs" className="group">
                <div className="p-6 border-2 border-dashed border-gray-300 rounded-2xl text-center hover:border-green-400 hover:bg-green-50 transition-all duration-200 hover:shadow-lg">
                  <div className="w-12 h-12 mx-auto mb-4 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-200 transition-colors">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-green-600 mb-1">Browse Jobs</p>
                  <p className="text-xs text-gray-500">Search for the best talent</p>
                </div>
              </Link>
            </div>
          </div>

          {/* Jobs and Pipeline */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="lg:col-span-2 bg-white rounded-2xl p-5 sm:p-8 shadow-lg border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-primary-black">Open Roles</h2>
                <Link to="/company/jobs">
                  <Button variant="ghost" size="sm" className="text-primary-lightGreen hover:text-primary-teaGreen">
                    View all
                  </Button>
                </Link>
              </div>
              <div className="space-y-4">
                {(loading ? [] : jobs).map((j) => (
                  <div key={j.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
                    <div className="flex-1 mb-3 sm:mb-0">
                      <h3 className="font-semibold text-primary-black text-lg">{j.title}</h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                          </svg>
                          {j.applicants} applicants
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          j.status === 'Open' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {j.status}
                        </span>
                      </div>
                    </div>
                    <Link to={`/company/jobs/${j.id}/edit`}>
                      <Button size="sm" variant="outline" className="w-full sm:w-auto">
                        Manage
                      </Button>
                    </Link>
                  </div>
                ))}
                {!loading && jobs.length === 0 && (
                  <div className="text-sm text-gray-600">No jobs found.</div>
                )}
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-5 sm:p-8 shadow-lg border border-gray-100">
              <h2 className="text-xl font-semibold text-primary-black mb-6">Pipeline Overview</h2>
              <div className="space-y-4">
                {(loading ? [] : pipeline).map((p, index) => (
                  <div key={p.stage} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">{p.stage}</span>
                      <span className="text-sm font-bold text-primary-black">{p.count}</span>
                    </div>
                    <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-3 rounded-full transition-all duration-500 ${
                          index === 0 ? 'bg-gradient-to-r from-primary-lightGreen to-primary-teaGreen' :
                          index === 1 ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' :
                          index === 2 ? 'bg-gradient-to-r from-purple-500 to-purple-600' :
                          'bg-gradient-to-r from-green-500 to-green-600'
                        }`}
                        style={{ width: `${pipeline.length ? Math.min(100, (p.count / Math.max(...pipeline.map(pipe => pipe.count))) * 100) : 0}%` }}
                      />
                    </div>
                  </div>
                ))}
                {!loading && pipeline.length === 0 && (
                  <div className="text-sm text-gray-600">No pipeline data.</div>
                )}
              </div>
              
              <div className="mt-6 p-4 bg-gradient-to-r from-primary-lightGreen/10 to-primary-teaGreen/10 rounded-xl">
                <h3 className="font-semibold text-primary-black mb-2">Total Candidates</h3>
                <p className="text-2xl font-bold text-primary-black">
                  {(loading ? 0 : pipeline.reduce((sum, p) => sum + p.count, 0))}
                </p>
                <p className="text-sm text-gray-600 mt-1">Across all stages</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
